import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { ELEMENT_TYPES, LAYER_COLORS, STATUS_OPTIONS, RELEASE_CANDIDATE_OPTIONS } from "@/constants/archimate";
import { Trash2, Target, Crosshair, Plus } from "lucide-react";

export function ElementDetailsDialog({ open, onOpenChange, element, relationships, elementsById, owners, onSave, onDelete, onFocusElement }) {
  const [name, setName] = useState("");
  const [documentation, setDocumentation] = useState("");
  const [status, setStatus] = useState("Dev");
  const [releaseCandidate, setReleaseCandidate] = useState("");
  const [owner, setOwner] = useState("");
  const [addingNewOwner, setAddingNewOwner] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (element) {
      setName(element.name);
      setDocumentation(element.documentation || "");
      setStatus(element.status || "Dev");
      setReleaseCandidate(element.release_candidate || "");
      setOwner(element.owner || "");
      setAddingNewOwner(false);
    }
  }, [element]);

  if (!element) return null;
  const typeInfo = ELEMENT_TYPES[element.type] || {};
  const colors = LAYER_COLORS[element.layer] || LAYER_COLORS.business;
  const ownerOptions = Array.from(new Set([...(owners || []), ...(owner ? [owner] : [])])).sort();
  const realizedBy = (relationships || [])
    .filter((r) => r.type === "realization" && r.target_id === element.id)
    .map((rel) => ({ rel, realizer: elementsById[rel.source_id] }))
    .filter((r) => r.realizer);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="element-details-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {element.name}
              <Badge className={colors.badge} variant="outline">
                {typeInfo.label}
              </Badge>
            </DialogTitle>
            <DialogDescription>View and edit this element's details, or delete it from the model.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Name</label>
              <Input data-testid="element-details-name-input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Documentation</label>
              <Textarea
                data-testid="element-details-documentation-input"
                value={documentation}
                onChange={(e) => setDocumentation(e.target.value)}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Status</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="element-details-status-select">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Release Candidate</label>
                <Select value={releaseCandidate || "__unset__"} onValueChange={(v) => setReleaseCandidate(v === "__unset__" ? "" : v)}>
                  <SelectTrigger data-testid="element-details-release-candidate-select">
                    <SelectValue placeholder="Unset" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__unset__">Unset</SelectItem>
                    {RELEASE_CANDIDATE_OPTIONS.map((rc) => (
                      <SelectItem key={rc} value={rc}>
                        {rc}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Owner</label>
              <Select
                value={addingNewOwner ? "__add_new__" : owner || "__unset__"}
                onValueChange={(v) => {
                  if (v === "__add_new__") {
                    setAddingNewOwner(true);
                    setOwner("");
                  } else if (v === "__unset__") {
                    setAddingNewOwner(false);
                    setOwner("");
                  } else {
                    setAddingNewOwner(false);
                    setOwner(v);
                  }
                }}
              >
                <SelectTrigger data-testid="element-details-owner-select">
                  <SelectValue placeholder="No owner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unset__">No owner</SelectItem>
                  {ownerOptions.map((o) => (
                    <SelectItem key={o} value={o}>
                      {o}
                    </SelectItem>
                  ))}
                  <SelectItem value="__add_new__">
                    <span className="flex items-center gap-1.5">
                      <Plus size={12} /> Add new owner
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              {addingNewOwner && (
                <Input
                  autoFocus
                  data-testid="element-details-new-owner-input"
                  className="mt-2"
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                  placeholder="Type a new owner name"
                />
              )}
            </div>
            {element.type === "capability" && (
              <div>
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <Target size={12} />
                  Realized By
                </label>
                <div className="max-h-40 overflow-y-auto space-y-1.5 mt-1">
                  {realizedBy.length === 0 && (
                    <div className="text-xs text-slate-400 italic py-1" data-testid="realized-by-empty">
                      No elements realize this capability yet
                    </div>
                  )}
                  {realizedBy.map(({ rel, realizer }) => {
                    const rColors = LAYER_COLORS[realizer?.layer] || LAYER_COLORS.business;
                    const rTypeInfo = ELEMENT_TYPES[realizer?.type] || {};
                    return (
                      <div
                        key={rel.id}
                        data-testid={`realized-by-row-${rel.id}`}
                        className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-2 py-1.5 bg-slate-50"
                      >
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Badge className={rColors.badge} variant="outline">
                            {rTypeInfo.label || realizer?.type}
                          </Badge>
                          <span className="text-xs font-medium text-slate-700 truncate">{realizer?.name || "Unknown"}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs shrink-0"
                          data-testid={`realized-by-focus-button-${rel.id}`}
                          onClick={() => onFocusElement(realizer.id)}
                        >
                          <Crosshair size={12} className="mr-1" />
                          Focus
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {relationships?.length > 0 && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Relationships ({relationships.length})</label>
                <div className="max-h-32 overflow-y-auto space-y-1 mt-1">
                  {relationships.map((r) => {
                    const isSource = r.source_id === element.id;
                    const other = elementsById[isSource ? r.target_id : r.source_id];
                    return (
                      <div key={r.id} className="text-xs text-slate-600 flex items-center gap-1" data-testid={`element-relationship-row-${r.id}`}>
                        <span className="text-slate-400">{isSource ? "\u2192" : "\u2190"}</span>
                        <span className="font-medium">{r.type}</span>
                        <span>{other?.name || "Unknown"}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex sm:justify-between">
            <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)} data-testid="element-details-delete-button">
              <Trash2 size={14} className="mr-1" /> Delete
            </Button>
            <Button
              onClick={() => {
                onSave(element.id, { name, documentation, status, release_candidate: releaseCandidate, owner: owner.trim() });
                onOpenChange(false);
              }}
              data-testid="element-details-save-button"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="element-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{element.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the element and every relationship connected to it. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="element-delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="element-delete-confirm-button"
              onClick={() => {
                onDelete(element.id);
                onOpenChange(false);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
